/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 图片列表项组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-11 11:00:00
 */

import React, { memo } from 'react'
import { Checkbox, Tooltip } from 'antd'
import {
  PhotoItem,
  WatermarkExtractionStatus
} from '../../context/WatermarkBatchContext'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import './ImageItem.scss'
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons'

interface ImageItemProps {
  photo: PhotoItem
  originalIndex: number
  currentIndex: number
  isChecked: boolean
  isExtracting: boolean
  thumbWidth?: number
  onSelect: (index: number) => void
  onToggleCheck: (photo: PhotoItem, e: React.MouseEvent) => void
  onReExtract?: (photo: PhotoItem, e: React.MouseEvent) => void
  itemRef?: (el: HTMLDivElement | null) => void
}

const ImageItem: React.FC<ImageItemProps> = memo(
  ({
    photo,
    originalIndex,
    currentIndex,
    isChecked,
    isExtracting,
    thumbWidth,
    onSelect,
    onToggleCheck,
    onReExtract,
    itemRef
  }) => {
    const { buildThumbUrl } = useImageProcessUrl()
    const isFailed = photo.watermarkStatus === WatermarkExtractionStatus.FAILED
    const isExtractingTimeout =
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING_TIMEOUT
    const isEditFailed =
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED
    const isEditCompleted =
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    const isEditNoChanges =
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES
    const isEditTimeout =
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_TIMEOUT

    const rawThumbUrl = photo.aiURL || photo.smallURL || photo.url || ''
    const thumbnailUrl = buildThumbUrl(rawThumbUrl, thumbWidth)

    const handleThumbError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      const target = e.currentTarget
      const fallback = target.getAttribute('data-fallback')
      if (fallback && target.src !== fallback) {
        target.src = fallback
      }
    }

    return (
      <div
        ref={itemRef}
        className={`imageItem ${
          originalIndex === currentIndex ? 'active' : ''
        } ${isExtracting ? 'extracting' : ''} ${
          isExtractingTimeout ? 'extracting-timeout' : ''
        }`}
        onClick={() => onSelect(originalIndex)}
      >
        <div className="imageWrapper">
          <img
            src={thumbnailUrl}
            className="thumbnail"
            loading="lazy"
            decoding="async"
            data-fallback={rawThumbUrl}
            onError={handleThumbError}
            alt=""
          />

          {/* 右上角选中复选框 - 提取中时不显示 */}
          {!isExtracting && (
            <div
              className={`photoCheckbox ${
                isChecked ? 'photoCheckbox--checked' : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isChecked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => onToggleCheck(photo, e as any)}
              />
            </div>
          )}

          {/* 状态标签 */}
          {isFailed && (
            <Tooltip
              title={photo.errorMsg || photo.show_reason || '水印提取失败'}
              placement="top"
            >
              <div className="statusLabel status-failed">水印提取失败</div>
            </Tooltip>
          )}

          {isExtractingTimeout && (
            <>
              <Tooltip
                title={photo.errorMsg || photo.show_reason || '水印提取超时'}
                placement="top"
              >
                <div className="statusLabel status-extracting-timeout">
                  水印提取超时
                </div>
              </Tooltip>
              {/* Hover 时显示的刷新按钮 */}
              {onReExtract && (
                <div
                  className="refreshButton"
                  onClick={(e) => onReExtract(photo, e)}
                >
                  <ReloadOutlined />
                  <span>刷新</span>
                </div>
              )}
            </>
          )}

          {isEditFailed && (
            <Tooltip
              title={photo.errorMsg || photo.show_reason || '编辑失败'}
              placement="top"
            >
              <div className="statusLabel status-edit-failed">编辑失败</div>
            </Tooltip>
          )}

          {isEditCompleted && (
            <div className="statusLabel status-edit-completed">已编辑</div>
          )}

          {isEditNoChanges && (
            <div className="statusLabel status-edit-no-changes">无修改项</div>
          )}

          {isEditTimeout && (
            <div className="statusLabel status-edit-timeout">编辑超时</div>
          )}

          {/* 提取中遮罩 */}
          {isExtracting && (
            <div className="extractingOverlay">
              <LoadingOutlined
                style={{
                  fontSize: 32,
                  color: '#fff'
                }}
              />
              <div className="extractingText">水印提取中</div>
            </div>
          )}
        </div>
      </div>
    )
  },
  (prevProps, nextProps) => {
    // 自定义比较函数，只在关键属性变化时重新渲染
    // 如果返回 true，表示 props 相同，不重新渲染
    // 如果返回 false，表示 props 不同，需要重新渲染

    // 比较照片对象的关键属性
    const photoChanged =
      prevProps.photo.seq !== nextProps.photo.seq ||
      prevProps.photo.watermarkStatus !== nextProps.photo.watermarkStatus ||
      prevProps.photo.errorMsg !== nextProps.photo.errorMsg ||
      prevProps.photo.aiURL !== nextProps.photo.aiURL ||
      prevProps.photo.smallURL !== nextProps.photo.smallURL ||
      // watermarkData 是数组，比较长度和引用（如果引用相同说明没变化）
      prevProps.photo.watermarkData !== nextProps.photo.watermarkData

    // 比较其他 props
    const otherPropsChanged =
      prevProps.originalIndex !== nextProps.originalIndex ||
      prevProps.currentIndex !== nextProps.currentIndex ||
      prevProps.isChecked !== nextProps.isChecked ||
      prevProps.isExtracting !== nextProps.isExtracting

    // 如果都没有变化，返回 true（不重新渲染）
    return !photoChanged && !otherPropsChanged
  }
)

ImageItem.displayName = 'ImageItem'

export default ImageItem
